import { AppstoreAddOutlined, DeleteOutlined, DownOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Dropdown, Menu, Space } from 'antd';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type FiservPaymentModuleSpecsFragment } from '../../../../api/fragments/FiservPaymentModuleSpecs';
import { type PermissionValues } from '../../../../utilities/hasPermissions';
import useDeleteModule from '../useDeleteModule';
import { useFiservPaymentSettingsDrawer } from './FiservPaymentSettingsDrawer';
import { useUpdateFiservPaymentModuleDrawer } from './UpdateFiservPaymentModuleDrawer';

export type FiservPaymentModuleActionsProps = {
    module: FiservPaymentModuleSpecsFragment;
    permissions: Pick<PermissionValues, 'hasDeletePermission' | 'hasUpdatePermission'>;
};

const FiservPaymentModuleActions = ({ module, permissions }: FiservPaymentModuleActionsProps) => {
    const { t } = useTranslation(['fiservPaymentModuleDetails', 'moduleDetails']);
    const deleteModule = useDeleteModule(module.id);
    const addFiservPaymentSettingsDrawer = useFiservPaymentSettingsDrawer(module.id);

    const initialValues = useMemo(() => pick(['displayName'], module), [module]);
    const updateFiservPaymentModuleDrawer = useUpdateFiservPaymentModuleDrawer(module.id, initialValues);
    const { hasUpdatePermission, hasDeletePermission } = permissions;

    const items = [
        hasUpdatePermission && {
            label: t('fiservPaymentModuleDetails:mainActionButtons.updateSpecs'),
            key: 'updateSpecs',
            icon: <EditOutlined />,
            onClick: updateFiservPaymentModuleDrawer.open,
        },
        hasUpdatePermission && {
            label: t('fiservPaymentModuleDetails:mainActionButtons.addSetting'),
            key: 'addSetting',
            icon: <AppstoreAddOutlined />,
            onClick: addFiservPaymentSettingsDrawer.open,
        },
        hasDeletePermission && {
            label: t('fiservPaymentModuleDetails:mainActionButtons.delete'),
            key: 'delete',
            icon: <DeleteOutlined />,
            onClick: deleteModule,
            danger: true,
        },
    ].filter(Boolean);

    if (!items.length) {
        return null;
    }

    return (
        <>
            <Dropdown overlay={<Menu items={items} />}>
                <Button type="primary">
                    <Space>
                        <span>{t('fiservPaymentModuleDetails:mainActionButtons.actions')}</span>
                        <DownOutlined />
                    </Space>
                </Button>
            </Dropdown>
            {addFiservPaymentSettingsDrawer.render()}
            {updateFiservPaymentModuleDrawer.render()}
        </>
    );
};

export default FiservPaymentModuleActions;
